import { call } from '@packages/functions';
import { getRootElement } from '@packages/react/lib/rootUtils';
import { Record } from 'immutable';
import { set } from 'lodash';
import { createElement, isValidElement } from 'react';

export type PortalPointType = {
    id: string;
    portal: PortalType;
    name: string;
    order: number;
    zIndex: number;
};

export type PortalType = {
    id: string;
    name: string;
    points: { [key: string]: PortalPointType };
};

interface Event {
    data?: { [key: string]: any };
}

type resizePointOptions = {
    key?: string;
    elements?: string;
    readProp?: keyof HTMLElement | ((readElement: HTMLElement, writeElement: HTMLElement) => any);
    writeProp?: keyof HTMLElement | ((value: any, writeElement: HTMLElement, readElement: HTMLElement) => any);
    after?: (data: { event: Event; portal: HTMLElement; root: HTMLElement }) => void;
};

const MEMO = {};

function generateID(name: string, ...opts: any[]): string {
    const portalName = `${name}${opts && opts.length > 0 ? `__${opts.join('_')}` : ''}`;
    return `portal__${portalName}`;
}

function _elSetter(from: HTMLElement, to: HTMLElement, options: resizePointOptions) {
    let readValue: any;

    if (options.readProp) {
        readValue = typeof options.readProp === 'string' ? from[options.readProp] : call(options.readProp, from, to);
        if (options.writeProp) {
            if (typeof options.writeProp === 'string') {
                set(to, options.writeProp, readValue);
            } else {
                call(options.writeProp, readValue, to, from);
            }
        }
    }
}

export function portalID(name: string): string {
    return generateID(name);
}

export function mountPointID(portalName: string, pointName: string): string {
    return generateID(portalName, 'point', pointName);
}

export function getPortalElement(portalName: string): HTMLElement | null {
    if (typeof document !== 'undefined') {
        return document.getElementById(portalID(portalName)) as any;
    }

    return null;
}

export function getPortalPointElement(portalName: string, pointName: string): HTMLElement | null {
    if (typeof document !== 'undefined') {
        return document.getElementById(mountPointID(portalName, pointName)) as any;
    }

    return null;
}

const Portal = Record<PortalType>({
    id: '',
    name: '',
    points: {},
});

const PortalPoint = Record<PortalPointType>({
    id: '',
    name: '',
    portal: Portal(),
    order: 0,
    zIndex: 0,
});

export const createPortal = (portal: string): Record<PortalType> =>
    Portal({
        id: portalID(portal),
        name: portal,
    });

export const createPortalPoint = (data: PortalPointType): Record<PortalPointType> => PortalPoint(data);

export const handleResizePoint = (portalName: string, options: resizePointOptions = {}) => {
    const key = `${portalName}_${options.key}`;

    if (!MEMO[key]) {
        MEMO[key] = (event: Event) => {
            const root = getRootElement();
            const portal = getPortalElement(portalName);

            if (!root || !portal) {
                return;
            }

            _elSetter(portal, root, options);
            if (options.elements) {
                const elements = document.querySelectorAll(options.elements);
                if (elements) {
                    elements.forEach((element) => _elSetter(portal, element as any, options));
                }
            }

            call(options.after, { event, portal, root });
        };
    }

    return MEMO[key];
};

export const renderMountChildren = (children: any, component: any, rest: any = {}) => {
    const ChildrenComponent = children ? children : component;
    return ChildrenComponent
        ? isValidElement(ChildrenComponent)
            ? ChildrenComponent
            : createElement(ChildrenComponent, rest)
        : null;
};
